/**
 * Format a date to ISO string (YYYY-MM-DD)
 */
export function formatDate(date: Date): string {
  return date.toISOString().split('T')[0];
}

/**
 * Parse an ISO date string to Date object
 */
export function parseDate(dateString: string): Date {
  return new Date(dateString + 'T00:00:00');
}

/**
 * Get today's date formatted as ISO string
 */
export function getToday(): string {
  return formatDate(new Date());
}

/**
 * Get yesterday's date formatted as ISO string
 */
export function getYesterday(): string {
  const date = new Date();
  date.setDate(date.getDate() - 1);
  return formatDate(date);
}

/**
 * Check if a date is today
 */
export function isToday(dateString: string): boolean {
  return dateString === getToday();
}

/**
 * Check if a date is yesterday
 */
export function isYesterday(dateString: string): boolean {
  return dateString === getYesterday();
}

/**
 * Format a date for display (e.g., "Monday, Dec 15")
 */
export function formatDateDisplay(dateString: string, locale = 'en-US'): string {
  const date = parseDate(dateString);
  return date.toLocaleDateString(locale, {
    weekday: 'long',
    month: 'short',
    day: 'numeric',
  });
}

/**
 * Calculate the difference in days between two dates
 */
export function daysDifference(date1: string, date2: string): number {
  const d1 = parseDate(date1);
  const d2 = parseDate(date2);
  const diffTime = Math.abs(d2.getTime() - d1.getTime());
  return Math.floor(diffTime / (1000 * 60 * 60 * 24));
}

// ============================================================================
// Mood Color Utilities
// ============================================================================

/**
 * Mood color gradient: Red (0%) -> Yellow (50%) -> Green (100%)
 * Use this for mood percentages, mood badges, and mood-related visualizations.
 */

export interface MoodColorRGB {
  r: number;
  g: number;
  b: number;
}

/**
 * Get RGB values for a mood percentage (0-100)
 * Returns red at 0%, yellow at 50%, green at 100%
 */
export function getMoodColorRGB(mood: number): MoodColorRGB {
  const clampedMood = Math.max(0, Math.min(100, mood));

  if (clampedMood <= 50) {
    // Red to Yellow (0-50)
    const ratio = clampedMood / 50;
    return {
      r: 255,
      g: Math.round(180 * ratio),
      b: 0,
    };
  } else {
    // Yellow to Green (50-100)
    const ratio = (clampedMood - 50) / 50;
    return {
      r: Math.round(255 * (1 - ratio)),
      g: Math.round(180 + 75 * ratio),
      b: Math.round(50 * ratio),
    };
  }
}

/**
 * Get CSS rgb() string for a mood percentage
 */
export function getMoodColor(mood: number): string {
  const { r, g, b } = getMoodColorRGB(mood);
  return `rgb(${r}, ${g}, ${b})`;
}

/**
 * Get appropriate text color (white or dark) for a mood background
 */
export function getMoodTextColor(mood: number): string {
  // Use white text for very low (<40%) and high (>70%) mood values
  // Use dark text for middle values where yellow is brightest
  if (mood < 40 || mood > 70) {
    return '#ffffff';
  }
  return '#333333';
}

/**
 * Get complete style object for a mood badge
 */
export function getMoodBadgeStyle(mood: number): React.CSSProperties {
  return {
    backgroundColor: getMoodColor(mood),
    color: getMoodTextColor(mood),
  };
}

/**
 * Predefined mood range colors for categorical mood distribution
 * Maps mood ranges to their representative colors
 */
export const MOOD_RANGE_COLORS = {
  very_low: 'rgb(255, 36, 0)',   // ~10% - red
  low: 'rgb(255, 144, 0)',       // ~30% - orange-red
  neutral: 'rgb(255, 180, 0)',   // ~50% - yellow
  good: 'rgb(153, 210, 30)',     // ~70% - yellow-green
  great: 'rgb(50, 205, 50)',     // ~90% - green
} as const;

/**
 * Get mood range color by range name
 */
export function getMoodRangeColor(range: keyof typeof MOOD_RANGE_COLORS): string {
  return MOOD_RANGE_COLORS[range] || MOOD_RANGE_COLORS.neutral;
}
